import {
    Component, ElementRef, EventEmitter, Input, Inject, OnDestroy, OnInit,
    Output, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, AfterViewInit,
} from '@angular/core';


import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { DOCUMENT } from '@angular/common';

import { SIMPLE_CONFIRM_MODAL_NAME } from '../modal/modal-names';
import { PreferencesService } from '../preferences/preferences.service';

import { AppModalService } from '../modal/modal.service';
import { CommentsService } from './comments.service';
import { UserService } from '../user/user.service';
import { PermissionsService } from '../permissions/permissions.service';
import {first} from 'rxjs/operators';
import { takeWhile } from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'comments-component',
  templateUrl: 'comments.component.html',
  styleUrls: ['comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() comments: Array<any> = [];
  @Input() itemId: any; // partNumber for Item and origin_id for Asset
  @Input() brandId: any;
  @Input() itemType: string;

  @Output() commentPosted: EventEmitter<any> = new EventEmitter<any>();
  @Output() commentEdited: EventEmitter<any> = new EventEmitter<any>();
  @Output() commentResolved: EventEmitter<any> = new EventEmitter<any>();
  @Output() commentMarkedAsRead: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('editComment', {static: false})
  editComment: ElementRef | void;

  @ViewChild('writeCommentTextarea', {static: false})
  writeCommentTextarea: ElementRef | void;

  archiveConfirmTitle: string = 'Resolve comment?';
  cancelArchiveTitle: string = 'Cancel';
  confirmArchiveTitle: string = 'Resolve';
  /* eslint-disable */
  archiveNote: string = 'Are you sure you want to resolve this comment? This action cannot be undone.';
  /* eslint-enable */
  resolvedComment: any;
  writeCommentCtrl: UntypedFormControl;
  editingComment: boolean = false;
  cancelling: boolean = false;
  isActive: boolean = true;
  currentUserId: any;
  commentsFetched: boolean = false;
  paginationModel: any = {
    page: 1,
    per_page: 100,
  };
  private timezone: string;

  constructor(
    private appModalService: AppModalService,
    @Inject(DOCUMENT) private document: any,
    private commentsService: CommentsService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private ref: ChangeDetectorRef,
    private preferencesService: PreferencesService,
  ) {}

  get showTextareaHint(): boolean {
    return this.writeCommentCtrl && this.writeCommentCtrl.value;
  }

  ngOnInit() {
    this.writeCommentCtrl = new UntypedFormControl();
    this.userService.getUser().pipe(first()).subscribe((user: any) => {
      this.currentUserId = _.get(user, 'id');
    });

    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });
  }

  ngOnDestroy() {
    this.resolvedComment = null;
    this.writeCommentCtrl = null;
    this.comments = [];
    this.isActive = false;
  }

  ngAfterViewInit() {
    _.defer(() => {
      if (this.writeCommentTextarea) {
        this.writeCommentTextarea.nativeElement.focus();
      }
    });
  }

  // handlers
  onResolveClick(comment: any) {
    this.resolvedComment = comment;
    this.appModalService.showModal(
      SIMPLE_CONFIRM_MODAL_NAME,
      {
        primaryBtnTitle: this.cancelArchiveTitle,
        secondaryBtnTitle: this.confirmArchiveTitle,
        note: this.archiveNote,
        title: this.archiveConfirmTitle,
        version2: true,
      },
    );
    this.subscribeConfirmResolve();
  }

  onPostCommentClick() {
    this.postComment();
  }

  onNewCommentEnter(event: any) {
    event.preventDefault();
    this.postComment();
  }

  onCommentEnter(event: any, comment: any) {
    event.preventDefault();
    comment.value = this.getEditingCommentText();
    this.saveEditedComment(comment);
  }

  onEditCommentClick(comment: any) {
    if (this.editingComment) { return; }
    this.editingComment = true;
    comment.previous_value = comment.value;
    comment.editing = true;
    _.delay(() => {
      if (this.editComment) {
        this.setEndOfContenteditable(this.editComment.nativeElement);
      }
    }, 300);
  }

  onCancelEdit(event: any, comment: any) {
    event.preventDefault();
    this.cancelling = true;
    this.cancelEdit(comment);
  }

  onCommentInlineChange(comment: any) {
    _.delay(() => {
      if (this.cancelling) {
        this.cancelling = false;
        return;
      }
      if (!comment.editing) { return; }
      comment.value = this.getEditingCommentText();
      this.saveEditedComment(comment);
    }, 400);
  }

  onCommentTextClick(comment: any) {
    if (_.includes(comment.read_by, this.currentUserId)) {
      return;
    }
    this.commentsService.markAsRead(this.brandId, [comment._id])
    .subscribe(() => {
      comment.read_by.push(this.currentUserId);
      this.commentMarkedAsRead.emit({comment});
      this.ref.markForCheck();
    });
  }

  onCancelEditClick(event: any, comment: any) {
    event.preventDefault();
    this.cancelling = true;
    this.cancelEdit(comment);
  }

  // public
  getTooltipHtml(comment: any) {
    // TODO: add last_updated
    // <li>${formatDate(comment.user.last_updated)}</li>
    return `
      <ul>
        <li>${comment.user.first_name} ${comment.user.last_name}</li>
        ${comment.user.title ? `<li>${comment.user.title}</li>` : ''}
      </ul>
    `;
  }

  fetch(brandId?: any, itemId?: any, itemType?: string) {
    if (brandId) { this.brandId = brandId; }
    if (itemId) { this.itemId = itemId; }
    if (itemType) { this.itemType = itemType; } // 'asset'/'item'/'receiver'
    this.commentsService
    .fetchItemComments(
      this.brandId,
      this.itemId,
      this.itemType,
      this.paginationModel.per_page,
      this.paginationModel.page,
    )
    .pipe(first())
    .subscribe((data: any) => {
      if (!this.commentsFetched) { this.commentsFetched = true; }
      this.setData(data);
    });
  }

  isUnread(comment: any): boolean {
    return !_.includes(comment.read_by, this.currentUserId);
  }

  hasAdminPermission(): boolean {
    return this.permissionsService.hasPermission('admin');
  }

  // private
  private setData(data: any): void {
    this.comments = _.map(data.data, (item: any) => {
      item.value = _.unescape(item.value);
      return item;
    });
    this.ref.markForCheck();
  }

  private subscribeConfirmResolve(): void {
    this.appModalService.activeModal.secondaryBtnClick
    .pipe(first(), takeWhile(() => !!this.appModalService.activeModal))
    .subscribe(() => {
      this.commentsService.deleteComment(
        this.brandId,
        this.resolvedComment._id,
      )
      .subscribe(() => {
        this.commentResolved.emit({ comment: this.resolvedComment });
        this.fetch();
      });
    });
  }

  private postComment(): void {
    if (!this.writeCommentCtrl.value) { return; }
    this.commentsService.postComment(
      this.brandId,
      this.writeCommentCtrl.value,
      this.itemType,
      this.itemId,
    )
    .pipe(first())
    .subscribe((data: any) => {
      this.commentPosted.emit({ value: this.writeCommentCtrl.value });
      this.writeCommentCtrl.reset('');
      this.fetch();
    });
  }

  private setEndOfContenteditable(contentEditableElement: any): void {
    if (document.createRange) {
      const elRange = document.createRange();
      elRange.selectNodeContents(contentEditableElement);
      elRange.collapse(false);
      const elSelection = (<any>window).getSelection();
      elSelection.removeAllRanges();
      elSelection.addRange(elRange);
    }
  }

  private saveEditedComment(comment: any): void {
    this.editingComment = false;
    comment.editing = false;
    if (comment.previous_value === comment.value) { return; }
    comment.previous_value = comment.value;
    this.commentsService.putComment(this.brandId, comment._id, comment.value)
    .subscribe((data: any) => {
      this.ref.markForCheck();
      this.commentEdited.emit({ comment });
    });
  }

  private cancelEdit(comment: any): void {
    this.editingComment = false;
    comment.value = comment.previous_value;
    comment.editing = false;
    this.ref.markForCheck();
  }

  private getEditingCommentText(): string {
    return this.editComment && this.editComment.nativeElement.textContent;
  }
}
